import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { CheckCircle2, Ruler, ClipboardList, Sparkles, Check } from 'lucide-react';
import { getUniformPriceBySize } from '../../utils/pricing';

interface UniformSizeChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSize?: string;
  onSelectSize?: (size: string) => void;
  availableSizes?: string[];
  sizePrices?: Record<string, number>;
  basePrice?: number;
  productTitle?: string;
}

interface SizeRow {
  size: string;
  height: string;
  weight: string;
  category: 'Kanak-kanak' | 'Remaja / Dewasa' | 'Dewasa';
  price: number;
}

const SIZE_CHART_DATA: SizeRow[] = [
  { size: '110cm', height: '100 - 110 cm', weight: '16 - 18 kg', category: 'Kanak-kanak', price: 120 },
  { size: '120cm', height: '110 - 120 cm', weight: '18 - 20 kg', category: 'Kanak-kanak', price: 120 },
  { size: '130cm', height: '120 - 130 cm', weight: '20 - 25 kg', category: 'Kanak-kanak', price: 120 },
  { size: '140cm', height: '130 - 140 cm', weight: '25 - 35 kg', category: 'Kanak-kanak', price: 130 },
  { size: '150cm', height: '140 - 150 cm', weight: '35 - 40 kg', category: 'Remaja / Dewasa', price: 130 },
  { size: '160cm', height: '150 - 160 cm', weight: '40 - 50 kg', category: 'Remaja / Dewasa', price: 130 },
  { size: '170cm', height: '160 - 170 cm', weight: '50 - 60 kg', category: 'Dewasa', price: 130 },
  { size: '180cm', height: '170 - 180 cm', weight: '60 - 75 kg', category: 'Dewasa', price: 140 },
  { size: '190cm', height: '175 - 180 cm', weight: '75 - 85 kg', category: 'Dewasa', price: 140 },
  { size: '200cm', height: '180 - 185 cm', weight: '85 - 90 kg', category: 'Dewasa', price: 140 },
  { size: '210cm', height: '185 - 190 cm', weight: '90 - 100 kg', category: 'Dewasa', price: 140 }
];

export const UniformSizeChartModal: React.FC<UniformSizeChartModalProps> = ({
  isOpen,
  onClose,
  selectedSize = '150cm',
  onSelectSize,
  availableSizes,
  sizePrices,
  basePrice,
  productTitle
}) => {
  const [currentSize, setCurrentSize] = useState<string>(selectedSize);

  // Dynamic Chart Rows based on customized variations/sizes and pricing
  const dynamicChartData = React.useMemo<SizeRow[]>(() => {
    const sizesToUse = availableSizes && availableSizes.length > 0
      ? availableSizes
      : SIZE_CHART_DATA.map(r => r.size);

    return sizesToUse.map(size => {
      const defaultInfo = SIZE_CHART_DATA.find(r => r.size.toLowerCase() === size.toLowerCase());

      // Determine price dynamically from customized sizePrices
      let price = 120;
      if (sizePrices && sizePrices[size] !== undefined && Number(sizePrices[size]) > 0) {
        price = Number(sizePrices[size]);
      } else if (defaultInfo) {
        price = defaultInfo.price;
      } else {
        const uniformPrice = getUniformPriceBySize(size);
        price = uniformPrice > 0 ? uniformPrice : (basePrice || 120);
      }

      if (defaultInfo) {
        return {
          ...defaultInfo,
          size,
          price
        };
      }

      // Height / weight inference for numeric cm sizes
      const num = parseInt(size.replace(/[^0-9]/g, ''), 10);
      if (!isNaN(num) && num > 50) {
        const height = `${num - 10} - ${num} cm`;
        let weight = '35 - 50 kg';
        let category: 'Kanak-kanak' | 'Remaja / Dewasa' | 'Dewasa' = 'Dewasa';
        if (num <= 130) {
          weight = `${Math.max(15, Math.round(num * 0.16))} - ${Math.round(num * 0.2)} kg`;
          category = 'Kanak-kanak';
        } else if (num <= 160) {
          weight = `${Math.round(num * 0.25)} - ${Math.round(num * 0.32)} kg`;
          category = 'Remaja / Dewasa';
        } else {
          weight = `${Math.round(num * 0.35)} - ${Math.round(num * 0.45)} kg`;
          category = 'Dewasa';
        }
        return { size, height, weight, category, price };
      }

      // Standard letter apparel sizes (XS, S, M, L, XL, etc.)
      const upper = size.toUpperCase();
      const standardSizesMap: Record<string, { height: string; weight: string; category: 'Kanak-kanak' | 'Remaja / Dewasa' | 'Dewasa' }> = {
        'XS': { height: '140 - 150 cm', weight: '35 - 45 kg', category: 'Kanak-kanak' },
        'S': { height: '150 - 160 cm', weight: '45 - 55 kg', category: 'Remaja / Dewasa' },
        'M': { height: '160 - 170 cm', weight: '55 - 65 kg', category: 'Remaja / Dewasa' },
        'L': { height: '170 - 175 cm', weight: '65 - 75 kg', category: 'Dewasa' },
        'XL': { height: '175 - 180 cm', weight: '75 - 85 kg', category: 'Dewasa' },
        '2XL': { height: '180 - 185 cm', weight: '85 - 95 kg', category: 'Dewasa' },
        'XXL': { height: '180 - 185 cm', weight: '85 - 95 kg', category: 'Dewasa' },
        '3XL': { height: '185 - 195 cm', weight: '95 - 110 kg', category: 'Dewasa' },
        'XXXL': { height: '185 - 195 cm', weight: '95 - 110 kg', category: 'Dewasa' },
      };

      if (standardSizesMap[upper]) {
        return {
          size,
          ...standardSizesMap[upper],
          price
        };
      }

      return {
        size,
        height: 'Standard',
        weight: 'Potongan saiz biasa',
        category: 'Dewasa',
        price
      };
    });
  }, [availableSizes, sizePrices, basePrice]);

  useEffect(() => {
    if (selectedSize) {
      setCurrentSize(selectedSize);
    }
  }, [selectedSize]);

  const handleSelect = (size: string) => {
    setCurrentSize(size);
    if (onSelectSize) {
      onSelectSize(size);
    }
    onClose();
  };

  const selectedRow = dynamicChartData.find(r => r.size === currentSize) || dynamicChartData[0] || SIZE_CHART_DATA[4];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={productTitle ? `Carta Cadangan Saiz: ${productTitle}` : 'Carta Cadangan Saiz Uniform Silat ASMM'}
      maxWidth="max-w-2xl"
    >
      <div className="p-4 sm:p-6 space-y-5">
        {/* Header Notice */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-400 text-slate-950 flex items-center justify-center font-black shadow-xs shrink-0">
              <Ruler className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-sm font-black text-slate-900 tracking-tight uppercase">
                Cadangan Saiz Pakaian Silat
              </h4>
              <p className="text-xs text-slate-600">
                Pilih saiz daripada jadual untuk mengemaskini saiz tempahan anda secara automatik.
              </p>
            </div>
          </div>
        </div>

        {/* Text Mode Responsive Table */}
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-amber-400 text-slate-950 font-black sticky top-0 z-10 shadow-2xs">
                <tr>
                  <th className="py-3 px-3.5 sm:px-5 text-center">Saiz</th>
                  <th className="py-3 px-3.5 sm:px-5">Ketinggian Disyorkan</th>
                  <th className="py-3 px-3.5 sm:px-5">Berat Disyorkan</th>
                  <th className="py-3 px-3.5 sm:px-5 text-right">Harga</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {dynamicChartData.map((row) => {
                  const isSelected = currentSize === row.size;
                  return (
                    <tr
                      key={row.size}
                      onClick={() => handleSelect(row.size)}
                      className={`transition-all cursor-pointer ${isSelected
                        ? 'bg-amber-100/80 font-semibold ring-1 ring-inset ring-amber-400/60'
                        : 'hover:bg-amber-50/70 even:bg-slate-50/50'
                        }`}
                      title={`Klik untuk pilih saiz ${row.size}`}
                    >
                      <td className="py-2.5 px-3.5 sm:px-5 text-center">
                        <span className={`inline-block px-3 py-1.5 rounded-lg text-xs font-black transition-all ${isSelected
                          ? 'bg-slate-900 text-amber-300 ring-2 ring-amber-400 scale-105'
                          : 'bg-slate-900 text-white'
                          }`}>
                          {row.size}
                        </span>
                      </td>
                      <td className="py-2.5 px-3.5 sm:px-5 font-bold text-slate-800">
                        {row.height}
                      </td>
                      <td className="py-2.5 px-3.5 sm:px-5 text-slate-700 font-medium">
                        {row.weight}
                      </td>
                      <td className="py-2.5 px-3.5 sm:px-5 text-right">
                        <span className="font-black text-xs sm:text-sm text-asmm-red">
                          RM {row.price.toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Panduan Saiz Box */}
        <div className="bg-amber-50 border border-amber-200/90 rounded-2xl p-4 sm:p-4.5 space-y-2.5 shadow-2xs">
          <div className="flex items-center gap-2 text-slate-900 font-black text-xs uppercase tracking-wide">
            <ClipboardList className="w-4 h-4 text-amber-600" />
            <span>Panduan Saiz & Tips Memilih:</span>
          </div>

          <ul className="space-y-1.5 text-xs text-slate-700 pl-0.5">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>Ukuran di atas adalah cadangan piawai untuk rujukan sahaja.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>Sila rujuk carta padanan tinggi dan berat sebelum menetapkan pesanan uniform.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                Jika anda atau anak anda suka memakai pakaian yang <strong>lebih longgar</strong> bagi memudahkan pergerakan silat, sila pilih <strong>satu saiz lebih besar</strong>.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>Sila hubungi khidmat pelanggan atau ketua gelanggang sekiranya memerlukan bantuan nasihat saiz.</span>
            </li>
          </ul>
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between gap-3 pt-1 border-t border-slate-100 flex-wrap">
          <div className="text-xs text-slate-600 flex items-center gap-2">
            <span>Pilihan Semasa:</span>
            <span className="font-black text-slate-900 bg-slate-100 px-2.5 py-1 rounded-lg">
              {selectedRow ? `${selectedRow.size} (RM ${selectedRow.price.toFixed(2)})` : currentSize}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 bg-transparent border-none cursor-pointer"
            >
              Tutup
            </button>
            <button
              type="button"
              onClick={() => handleSelect(currentSize)}
              className="px-5 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white rounded-xl text-xs font-extrabold transition-all cursor-pointer border-none shadow-xs"
            >
              Sahkan Saiz {currentSize}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
